import { Alert, Badge, Button, Card, CardHeader, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { Unplug } from 'lucide-react';
import { ChannelIcon } from '../components/channel-icon.js';
import { api } from '../lib/api.js';
import { SectionHeader } from './layout.js';

interface IgChannel { id: string; username: string | null; status: string; expiresAt: string | null; createdAt: string }
const igQuery = queryOptions({ queryKey: ['integrations', 'instagram'], queryFn: () => api<IgChannel[]>('/integrations/instagram') });

const RESULT: Record<string, ['success' | 'warning' | 'danger', string]> = {
  ok: ['success', 'Instagram conectado. As mensagens do Direct já chegam em Conversas.'],
  cancelado: ['warning', 'A conexão foi cancelada no Instagram.'],
  expirado: ['warning', 'O link de conexão expirou. Tente de novo.'],
  erro: ['danger', 'Não foi possível conectar. Confira se a conta é profissional (empresa ou criador) e tente de novo.'],
};

/** Configurações › Canais: Instagram Direct da clínica (o WhatsApp é conectado pela plataforma). */
export function ChannelsSettingsPage() {
  const qc = useQueryClient();
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  const result = typeof search.instagram === 'string' ? RESULT[search.instagram] : undefined;
  const { data, isLoading } = useQuery(igQuery);
  const start = useMutation({ mutationFn: () => api<{ url: string }>('/integrations/instagram/start', { method: 'POST' }), onSuccess: (r) => { window.location.href = r.url; } });
  const disconnect = useMutation({ mutationFn: (id: string) => api<void>(`/integrations/instagram/${id}/disconnect`, { method: 'POST' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['integrations', 'instagram'] }) });
  const active = data?.filter((c) => c.status !== 'disconnected') ?? [];
  return (
    <div className="grid gap-6">
      <SectionHeader title="Canais" description="Por onde os pacientes falam com a clínica. Tudo cai no mesmo inbox, com o ícone do canal." />
      {result && <Alert tone={result[0]}>{result[1]}</Alert>}
      <Card>
        <CardHeader title="Instagram Direct" description="Conta profissional (empresa ou criador). Mensagens do Direct entram em Conversas; a resposta sai pelo Instagram, em até 24 h da última mensagem da pessoa." />
        <div className="grid gap-4 p-5">
          {isLoading ? <Spinner className="size-5 text-fg-subtle" label="Carregando" /> : active.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-3">
              <ChannelIcon kind="instagram" className="size-5 text-fg" />
              <span className="font-medium text-fg">@{c.username}</span>
              <Badge tone="success">Conectado</Badge>
              {c.expiresAt && <span className="text-[12.5px] text-fg-muted">acesso renovado automaticamente</span>}
              <Button size="sm" variant="ghost" className="ml-auto" loading={disconnect.isPending && disconnect.variables === c.id} onClick={() => disconnect.mutate(c.id)}><Unplug className="size-4" />Desconectar</Button>
            </div>
          ))}
          {start.error && <Alert tone="danger">{start.error.message}</Alert>}
          <div><Button variant={active.length ? 'secondary' : 'primary'} loading={start.isPending} onClick={() => start.mutate()}>
            <ChannelIcon kind="instagram" />{active.length ? 'Conectar outra conta' : 'Conectar Instagram'}
          </Button></div>
        </div>
      </Card>
      <Card className="flex items-center gap-3 p-5">
        <ChannelIcon kind="whatsapp" className="size-5 text-fg" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-fg">WhatsApp</p>
          <p className="text-[13px] text-fg-muted">Os números são conectados pela plataforma (QR code). Fale com o suporte para incluir outro número.</p>
        </div>
      </Card>
    </div>
  );
}
